import axios, { AxiosError, AxiosInstance } from 'axios';
import i18next from 'i18next';
import { desktopProxyUrl, isAuroraDesktop } from '../utils/desktopRuntime';

export const XTREAM_HEALTH_EVENT = 'ott:xtream-health';

export type XtreamHealthStatus = 'ok' | 'down';

export interface XtreamHealthEventDetail {
  status: XtreamHealthStatus;
  httpStatus?: number;
  code?: string;
}

interface ProxyTokenResult {
  token: string;
  expiresAt: number;
}

type ProxyTokenProvider = () => Promise<ProxyTokenResult | null>;

export interface XtreamUser {
  status: string;
  exp_date: number;
  active_cons: number;
  max_connections: number;
  ip: string;
}

export interface XtreamCategory {
  category_id: string;
  category_name: string;
}

export interface UICategory {
  id: string;
  name: string;
  type: 'series' | 'movies' | 'livestreams';
  count: number;
}

export interface UISearchResult {
  id: string;
  title: string;
  type: 'series' | 'movie' | 'livestream';
  category: string;
  poster?: string;
}

export interface XtreamContent {
  stream_id?: string;  // Para live e movies
  series_id?: string;  // Para séries
  name: string;
  icon?: string;
  stream_icon?: string;  // Ícone do stream da API
  type?: string;
  duration?: string;
  stream_type?: string;  // 'series', 'live', 'movie'
  category_id?: string;  // ID da categoria
  container_extension?: string;
  is_adult?: string | boolean;
  cover?: string;
  tvg_logo?: string;
  tv_archive?: string | number;
  tv_archive_duration?: string | number;
  continueSubtitle?: string;
  /** URL direta do stream — quando presente, ignora a construção de URL Xtream. */
  direct_source?: string;
  /** Sinopse — presente na listagem de muitos servidores Xtream */
  plot?: string;
  /** Ano de estreia — campo 'year' (ex: "2022") */
  year?: string;
  /** Ano/data de estreia — campo 'releaseDate' (ex: "2022-10-21") */
  releaseDate?: string;
  /** Ano/data de estreia — campo 'release_date' (ex: "2022-10-21") */
  release_date?: string;
}

export interface XtreamEpisode {
  id: string;
  episode_num: number;
  title: string;
  season: number;
  air_date?: string;
  duration?: string;
  plot?: string;
  container_extension?: string;
  direct_source?: string;
  /** Nested info block from get_series_info response */
  info?: {
    plot?: string;
    movie_image?: string;
    releasedate?: string;
    duration_secs?: number;
    duration?: string;
    rating?: string;
  };
}

export interface XtreamSeason {
  [key: string]: XtreamEpisode[];
}

export interface XtreamSeriesInfo {
  info: {
    name: string;
    cover?: string;
    plot?: string;
    cast?: string;
    director?: string;
    rating?: string;
    youtube_trailer?: string;
    tmdb_id?: string | number;
    year?: string;
    releaseDate?: string;
    release_date?: string;
    releasedate?: string;
  };
  seasons?: XtreamSeason;  // Pode estar vazio
  episodes: XtreamSeason;  // Campo real com os episódios
}

export interface XtreamCurrentLiveProgram {
  title: string;
  description?: string;
  startMs: number;
  endMs: number;
}

type SearchScope = 'live' | 'movies' | 'series';
type CatalogRoute = 'https' | 'http' | 'worker';

interface SearchCacheBucket {
  itemsByCategory: Map<string, XtreamContent[]>;
  loadingPromise: Promise<void> | null;
}

class XtreamAPI {
  private api: AxiosInstance;
  private host: string = '';
  private username: string = '';
  private password: string = '';
  private protocol: string = 'http';
  private proxyTokenProvider: ProxyTokenProvider | null = null;
  private proxyTokenCache: ProxyTokenResult | null = null;
  private lastHealthEventAt = 0;
  private searchCache: Record<SearchScope, SearchCacheBucket> = {
    live: { itemsByCategory: new Map(), loadingPromise: null },
    movies: { itemsByCategory: new Map(), loadingPromise: null },
    series: { itemsByCategory: new Map(), loadingPromise: null },
  };

  constructor() {
    this.api = axios.create({
      timeout: 30000,
    });

    this.api.interceptors.request.use(async (config) => {
      if (this.isProxyEnabled()) {
        const token = await this.getProxyToken();
        if (token) {
          config.headers = config.headers || {};
          (config.headers as any)['X-Proxy-Token'] = token;
        }
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => {
        this.emitHealth({ status: 'ok' });
        return response;
      },
      (error: AxiosError) => {
        if (error.response?.status === 401 || error.response?.status === 403) this.proxyTokenCache = null;
        if (this.isServerUnavailableError(error)) {
          this.emitHealth({
            status: 'down',
            httpStatus: error.response?.status,
            code: error.code,
          });
        }
        return Promise.reject(error);
      },
    );
  }

  private emitHealth(detail: XtreamHealthEventDetail) {
    if (typeof window === 'undefined') return;
    if (detail.status === 'down') {
      const now = Date.now();
      if (now - this.lastHealthEventAt < 4000) return;
      this.lastHealthEventAt = now;
    }
    window.dispatchEvent(new CustomEvent<XtreamHealthEventDetail>(XTREAM_HEALTH_EVENT, { detail }));
  }

  private isServerUnavailableError(error: AxiosError): boolean {
    const status = error.response?.status;
    if (status === 401 || status === 403) return false;
    if (status && (status >= 500 || [520, 521, 522, 523, 524].includes(status))) return true;
    if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') return true;
    return !error.response;
  }

  setProxyTokenProvider(provider: ProxyTokenProvider | null) {
    this.proxyTokenProvider = provider;
    this.proxyTokenCache = null;
  }

  setCredentials(host: string, username: string, password: string, protocol?: string) {
    this.proxyTokenCache = null;
    const raw = host.trim().replace(/\/+$/, '');
    const schemeMatch = /^(https?):\/\//i.exec(raw);
    this.host = raw.replace(/^https?:\/\//i, '');
    this.username = username.trim();
    this.password = password.trim();
    this.protocol = protocol === 'https'
      ? 'https'
      : (schemeMatch?.[1].toLowerCase() === 'https' ? 'https' : 'http');
    this.resetSearchCache();
  }

  clearCredentials() {
    this.host = '';
    this.username = '';
    this.password = '';
    this.resetSearchCache();
  }

  clearRuntimeCache() {
    this.proxyTokenCache = null;
    this.resetSearchCache();
  }

  private isProxyEnabled(): boolean {
    return !isAuroraDesktop() && Boolean(process.env.REACT_APP_XTREAM_PROXY_URL);
  }

  private async getProxyToken(): Promise<string | null> {
    if (!this.proxyTokenProvider) {
      return null;
    }

    const now = Date.now();
    if (this.proxyTokenCache && this.proxyTokenCache.expiresAt - 30_000 > now) {
      return this.proxyTokenCache.token;
    }

    const nextToken = await this.proxyTokenProvider();
    if (!nextToken?.token) {
      return null;
    }

    this.proxyTokenCache = nextToken;
    return nextToken.token;
  }

  private resetSearchCache() {
    (Object.keys(this.searchCache) as SearchScope[]).forEach((scope) => {
      this.searchCache[scope].itemsByCategory.clear();
      this.searchCache[scope].loadingPromise = null;
    });
  }

  private catalogRouteStorageKey(): string {
    const identity = this.host.toLowerCase().replace(/\/+$/, '');
    let checksum = 0;
    for (let index = 0; index < identity.length; index += 1) {
      checksum = ((checksum << 5) - checksum + identity.charCodeAt(index)) | 0;
    }
    return `ott_catalog_route_v1_${Math.abs(checksum)}`;
  }

  private readCatalogRoute(): CatalogRoute | null {
    if (typeof localStorage === 'undefined') return null;
    try {
      const parsed = JSON.parse(localStorage.getItem(this.catalogRouteStorageKey()) || 'null') as { route?: CatalogRoute; expiresAt?: number } | null;
      if (!parsed?.route || !parsed.expiresAt || parsed.expiresAt <= Date.now()) return null;
      return parsed.route;
    } catch {
      return null;
    }
  }

  private writeCatalogRoute(route: CatalogRoute | null): void {
    if (typeof localStorage === 'undefined') return;
    try {
      if (!route) {
        localStorage.removeItem(this.catalogRouteStorageKey());
        return;
      }
      localStorage.setItem(this.catalogRouteStorageKey(), JSON.stringify({
        route,
        expiresAt: Date.now() + 24 * 60 * 60 * 1000,
      }));
    } catch {
      // Route discovery still works when storage is unavailable.
    }
  }

  private catalogBaseUrl(route: CatalogRoute): string {
    const directProtocol = route === 'worker' ? this.protocol : route;
    const direct = `${directProtocol}://${this.host}/player_api.php`;
    if (route !== 'worker') return desktopProxyUrl(direct);
    const proxyUrl = String(process.env.REACT_APP_XTREAM_PROXY_URL || '').replace(/\/+$/, '');
    if (!proxyUrl) throw new Error('Xtream Worker is not configured');
    return `${proxyUrl}/?url=${encodeURIComponent(direct)}`;
  }

  private async requestCatalog(action: string, categoryId?: string): Promise<XtreamContent[]> {
    const params = this.getParams(action);
    if (categoryId) params.category_id = categoryId;
    const cachedRoute = this.readCatalogRoute();
    const ordered: CatalogRoute[] = [];
    if (cachedRoute) ordered.push(cachedRoute);
    (['https', 'http', 'worker'] as CatalogRoute[]).forEach((route) => {
      if (ordered.indexOf(route) < 0 && (route !== 'worker' || this.isProxyEnabled())) ordered.push(route);
    });

    let lastError: unknown;
    for (let index = 0; index < ordered.length; index += 1) {
      const route = ordered[index];
      try {
        const response = route === 'worker'
          ? await this.api.get(this.catalogBaseUrl(route), { params, timeout: 15_000 })
          : await axios.get(this.catalogBaseUrl(route), { params, timeout: 15_000 });
        if (!Array.isArray(response.data)) throw new Error('Invalid Xtream catalog response');
        this.writeCatalogRoute(route);
        this.emitHealth({ status: 'ok' });
        return response.data;
      } catch (error) {
        lastError = error;
        if (cachedRoute === route) this.writeCatalogRoute(null);
      }
    }
    const axiosError = lastError as AxiosError | undefined;
    this.emitHealth({ status: 'down', httpStatus: axiosError?.response?.status, code: axiosError?.code });
    throw lastError instanceof Error ? lastError : new Error('Unable to download Xtream catalog');
  }

  private normalizeSearchText(value: string): string {
    let normalized = (value || '').toLowerCase().trim();
    const stringWithOptionalNormalize: any = normalized as any;
    if (stringWithOptionalNormalize && typeof stringWithOptionalNormalize.normalize === 'function') {
      try {
        normalized = stringWithOptionalNormalize.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      } catch (error) {
        // Some very old engines can throw on normalize; keep raw lowercase text.
      }
    }
    return normalized;
  }

  private async fetchScopeCategoryItems(scope: SearchScope, categoryId: string): Promise<XtreamContent[]> {
    if (scope === 'live') return this.getLiveStreamsForUI(categoryId);
    if (scope === 'movies') return this.getMoviesForUI(categoryId);
    return this.getSeriesForUI(categoryId);
  }

  private uniqueItems(items: XtreamContent[]): XtreamContent[] {
    const dedup = new Map<string, XtreamContent>();
    items.forEach((item) => {
      const id = String(item.stream_id || item.series_id || '');
      const key = `${id}|${item.category_id || ''}|${item.name || ''}`;
      if (!dedup.has(key)) dedup.set(key, item);
    });
    return Array.from(dedup.values());
  }

  cacheSearchItems(scope: SearchScope, categoryId: string, items: XtreamContent[]) {
    if (!categoryId) return;
    this.searchCache[scope].itemsByCategory.set(categoryId, this.uniqueItems(items));
  }

  getCachedSearchItems(scope: SearchScope): XtreamContent[] {
    const allItems: XtreamContent[] = [];
    this.searchCache[scope].itemsByCategory.forEach((items) => allItems.push(...items));
    return this.uniqueItems(allItems);
  }

  isSearchCacheWarm(scope: SearchScope, categoryIds: string[]): boolean {
    if (!categoryIds.length) return false;
    return categoryIds.every((categoryId) => this.searchCache[scope].itemsByCategory.has(categoryId));
  }

  async warmSearchCache(
    scope: SearchScope,
    categoryIds: string[],
    maxConcurrency: number = 3,
    onProgress?: (loaded: number, total: number, items: XtreamContent[]) => void
  ): Promise<XtreamContent[]> {
    const bucket = this.searchCache[scope];
    const totalCategories = categoryIds.length;
    const missingCategoryIds = categoryIds.filter((categoryId) => !bucket.itemsByCategory.has(categoryId));
    let loadedCount = totalCategories - missingCategoryIds.length;

    if (onProgress) {
      onProgress(loadedCount, totalCategories, this.getCachedSearchItems(scope));
    }

    if (!missingCategoryIds.length) {
      return this.getCachedSearchItems(scope);
    }

    if (bucket.loadingPromise) {
      await bucket.loadingPromise;
      return this.getCachedSearchItems(scope);
    }

    const workerCount = Math.max(1, Math.min(maxConcurrency, missingCategoryIds.length));
    let cursor = 0;

    bucket.loadingPromise = (async () => {
      const workers = Array.from({ length: workerCount }, async () => {
        while (cursor < missingCategoryIds.length) {
          const currentIndex = cursor;
          cursor += 1;
          const categoryId = missingCategoryIds[currentIndex];
          try {
            const items = await this.fetchScopeCategoryItems(scope, categoryId);
            this.cacheSearchItems(scope, categoryId, items);
          } catch (error) {
            console.warn(`⚠️ Falha ao aquecer cache global ${scope} categoria ${categoryId}`);
          } finally {
            loadedCount += 1;
            if (onProgress) {
              onProgress(loadedCount, totalCategories, this.getCachedSearchItems(scope));
            }
          }
        }
      });
      await Promise.all(workers);
    })();

    try {
      await bucket.loadingPromise;
    } finally {
      bucket.loadingPromise = null;
    }

    return this.getCachedSearchItems(scope);
  }

  private buildImageUrl(imagePath?: string): string {
    if (!imagePath) return '';
    if (/^https?:\/\//i.test(imagePath)) return imagePath;
    const cleanPath = imagePath.replace(/^\/+/, '');
    return `${this.protocol}://${this.host}/${cleanPath}`;
  }

  private mapCategory(category: XtreamCategory, type: UICategory['type']): UICategory {
    return {
      id: String(category.category_id || ''),
      name: category.category_name || i18next.t('common.unnamed'),
      type,
      count: 0,
    };
  }

  private mapContent(content: XtreamContent, type: 'series' | 'movie' | 'livestream'): XtreamContent {
    const resolvedId = String(content.stream_id || content.series_id || '');
    const resolvedIcon = this.buildImageUrl(content.stream_icon || content.icon || content.cover || content.tvg_logo || '');
    return {
      ...content,
      name: content.name || '',
      stream_id: resolvedId,
      series_id: type === 'series' ? resolvedId : content.series_id,
      icon: resolvedIcon,
      stream_type: type,
      category_id: String(content.category_id || ''),
      is_adult: content.is_adult ?? '0',
      container_extension: content.container_extension || (type === 'livestream' ? 'm3u8' : 'mp4'),
    };
  }

  private decodeMaybeBase64(value: unknown): string {
    const text = typeof value === 'string' ? value.trim() : '';
    if (!text) return '';

    if (typeof atob !== 'function') {
      return text;
    }

    // Attempt base64 decode only for likely payloads. Keep original on failure.
    if (!/^[A-Za-z0-9+/=\s]+$/.test(text)) {
      return text;
    }

    try {
      const normalized = text.replace(/\s+/g, '');
      const decoded = atob(normalized);
      if (!decoded) return text;
      // Handle UTF-8 titles when possible, but keep old-engine safe fallback.
      try {
        return decodeURIComponent(escape(decoded));
      } catch {
        return decoded;
      }
    } catch {
      return text;
    }
  }

  private parseTimestampToMs(value: unknown): number | null {
    if (typeof value === 'number' && isFinite(value)) {
      return value > 1000000000000 ? value : value * 1000;
    }

    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (/^\d+$/.test(trimmed)) {
      const parsed = parseInt(trimmed, 10);
      if (!isFinite(parsed)) return null;
      return parsed > 1000000000000 ? parsed : parsed * 1000;
    }

    // Strings no formato "YYYY-MM-DD HH:MM:SS" ou "YYYY-MM-DDTHH:MM:SS" sem indicador
    // de timezone são enviadas pelo Xtream no horário do servidor (normalmente local do
    // provedor). Para evitar que Date.parse() as interprete como UTC em alguns ambientes
    // (TV boxes / browsers antigos), fazemos o parse por componentes usando new Date()
    // que o JS SEMPRE trata como hora local — independente do engine.
    const noTzMatch = trimmed.match(
      /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})$/
    );
    if (noTzMatch) {
      const [, y, mo, d, h, mi, s] = noTzMatch.map(Number);
      const localDate = new Date(y, mo - 1, d, h, mi, s);
      if (!isNaN(localDate.getTime())) return localDate.getTime();
    }

    // Para strings com timezone explícito (Z, +HH:MM, -HH:MM) usa Date.parse normal.
    const normalized = trimmed.indexOf('T') >= 0 ? trimmed : trimmed.replace(' ', 'T');
    const parsed = Date.parse(normalized);
    if (!isNaN(parsed)) return parsed;

    const fallback = Date.parse(trimmed);
    if (!isNaN(fallback)) return fallback;

    return null;
  }

  private pickProgramTimestamp(item: any, kind: 'start' | 'end'): number | null {
    const startKeys = ['start_timestamp', 'start', 'start_datetime', 'start_time', 'start_ts'];
    const endKeys = ['stop_timestamp', 'end_timestamp', 'end', 'stop', 'end_datetime', 'stop_datetime', 'end_time', 'stop_time'];
    const keys = kind === 'start' ? startKeys : endKeys;

    for (let index = 0; index < keys.length; index += 1) {
      const key = keys[index];
      const parsed = this.parseTimestampToMs(item?.[key]);
      if (parsed !== null) return parsed;
    }
    return null;
  }

  private mapEPGItemToProgram(item: any): XtreamCurrentLiveProgram | null {
    if (!item || typeof item !== 'object') return null;

    const startMs = this.pickProgramTimestamp(item, 'start');
    const endMs = this.pickProgramTimestamp(item, 'end');
    if (startMs === null || endMs === null || endMs <= startMs) {
      return null;
    }

    const decodedTitle = this.decodeMaybeBase64(item.title || item.name || item.program_title || '');
    const decodedDescription = this.decodeMaybeBase64(item.description || item.plot || '');

    return {
      title: decodedTitle || i18next.t('liveTV.noProgram'),
      description: decodedDescription || undefined,
      startMs,
      endMs,
    };
  }

  private extractProgramsFromEPGResponse(data: any): XtreamCurrentLiveProgram[] {
    const candidatesRaw =
      (Array.isArray(data) ? data : null) ||
      (Array.isArray(data?.epg_listings) ? data.epg_listings : null) ||
      (Array.isArray(data?.epgListings) ? data.epgListings : null) ||
      (data?.now_playing ? [data.now_playing] : null) ||
      [];

    return candidatesRaw
      .map((item: any) => this.mapEPGItemToProgram(item))
      .filter((item: XtreamCurrentLiveProgram | null): item is XtreamCurrentLiveProgram => !!item)
      .sort((a: XtreamCurrentLiveProgram, b: XtreamCurrentLiveProgram) => a.startMs - b.startMs);
  }

  private getBaseURL(): string {
    const direct = `${this.protocol}://${this.host}/player_api.php`;
    if (isAuroraDesktop()) return desktopProxyUrl(direct);
    const proxyUrl = process.env.REACT_APP_XTREAM_PROXY_URL;
    if (proxyUrl) {
      return `${proxyUrl.replace(/\/$/, '')}/?url=${encodeURIComponent(direct)}`;
    }
    return direct;
  }

  private getParams(action?: string) {
    const params: any = {
      username: this.username,
      password: this.password,
    };
    if (action) params.action = action;
    return params;
  }

  async getAccountInfo(): Promise<XtreamUser> {
    const response = await this.api.get(this.getBaseURL(), {
      params: this.getParams(),
    });
    return response.data.user_info;
  }

  async getLiveCategories(): Promise<XtreamCategory[]> {
    const params = this.getParams('get_live_categories');
    const response = await this.api.get(this.getBaseURL(), { params });
    return Array.isArray(response.data) ? response.data : [];
  }

  async getLiveCategoriesForUI(): Promise<UICategory[]> {
    const categories = await this.getLiveCategories();
    return categories.map((category) => this.mapCategory(category, 'livestreams'));
  }

  async getMovieCategories(): Promise<XtreamCategory[]> {
    const params = this.getParams('get_vod_categories');
    const response = await this.api.get(this.getBaseURL(), { params });
    return Array.isArray(response.data) ? response.data : [];
  }

  async getSeriesCategories(): Promise<XtreamCategory[]> {
    const params = this.getParams('get_series_categories');
    const response = await this.api.get(this.getBaseURL(), { params });
    return Array.isArray(response.data) ? response.data : [];
  }

  async getLiveStreams(categoryId?: string): Promise<XtreamContent[]> {
    return this.requestCatalog('get_live_streams', categoryId);
  }

  async getMovies(categoryId?: string): Promise<XtreamContent[]> {
    return this.requestCatalog('get_vod_streams', categoryId);
  }

  async getMovieCategoriesForUI(): Promise<UICategory[]> {
    const categories = await this.getMovieCategories();
    return categories.map((category) => this.mapCategory(category, 'movies'));
  }

  async getSeries(categoryId?: string): Promise<XtreamContent[]> {
    return this.requestCatalog('get_series', categoryId);
  }

  async getSeriesCategoriesForUI(): Promise<UICategory[]> {
    const categories = await this.getSeriesCategories();
    return categories.map((category) => this.mapCategory(category, 'series'));
  }

  async getLiveStreamsForUI(categoryId?: string): Promise<XtreamContent[]> {
    const streams = await this.getLiveStreams(categoryId);
    return streams.map((stream) => this.mapContent(stream, 'livestream'));
  }

  async getCurrentLiveProgram(streamId: string, limit: number = 6): Promise<XtreamCurrentLiveProgram | null> {
    const resolvedStreamId = String(streamId || '').trim();
    if (!resolvedStreamId) return null;

    const params = this.getParams('get_short_epg');
    params.stream_id = resolvedStreamId;
    params.limit = String(limit);

    try {
      const response = await this.api.get(this.getBaseURL(), { params });
      const programs = this.extractProgramsFromEPGResponse(response?.data);

      if (!programs.length) return null;

      const now = Date.now();
      const current = programs.find((program: XtreamCurrentLiveProgram) => now >= program.startMs && now < program.endMs);
      return current || null;
    } catch {
      return null;
    }
  }

  async getLiveProgramSchedule(streamId: string, limit: number = 24): Promise<XtreamCurrentLiveProgram[]> {
    const resolvedStreamId = String(streamId || '').trim();
    if (!resolvedStreamId) return [];

    const params = this.getParams('get_short_epg');
    params.stream_id = resolvedStreamId;
    params.limit = String(limit);

    try {
      const response = await this.api.get(this.getBaseURL(), { params });
      return this.extractProgramsFromEPGResponse(response?.data);
    } catch {
      return [];
    }
  }

  /**
   * Fetches up to 7 days of EPG for a stream using get_simple_data_table.
   * Passes explicit start/end Unix timestamps so servers with record limits
   * return the full range instead of only the nearest N entries.
   * Window: today to +7 days.
   */
  async getLiveEpgFull(
    streamId: string,
  ): Promise<XtreamCurrentLiveProgram[]> {
    const resolvedStreamId = String(streamId || '').trim();
    if (!resolvedStreamId) return [];

    const now = Math.floor(Date.now() / 1000);
    const DAY = 86400;
    const start = now;
    const end   = now + 7 * DAY;

    const params = this.getParams('get_simple_data_table');
    params.stream_id = resolvedStreamId;
    params.start     = String(start);
    params.end       = String(end);

    try {
      const response = await this.api.get(this.getBaseURL(), { params });
      return this.extractProgramsFromEPGResponse(response?.data);
    } catch {
      return [];
    }
  }

  async getMoviesForUI(categoryId?: string): Promise<XtreamContent[]> {
    const streams = await this.getMovies(categoryId);
    return streams.map((stream) => this.mapContent(stream, 'movie'));
  }

  async getSeriesForUI(categoryId?: string): Promise<XtreamContent[]> {
    const streams = await this.getSeries(categoryId);
    return streams.map((stream) => this.mapContent(stream, 'series'));
  }

  getHost(): string {
    return this.host ? `${this.protocol}://${this.host}` : '';
  }

  getUsername(): string {
    return this.username;
  }

  async getVodInfo(vodId: string): Promise<any> {
    const params = this.getParams('get_vod_info');
    params.vod_id = vodId;
    try {
      const response = await this.api.get(this.getBaseURL(), { params });
      return response.data || null;
    } catch (err: any) {
      console.error('❌ Erro na requisição de VOD:', err.message);
      return null;
    }
  }

  searchInItems(items: XtreamContent[], query: string, limit: number = 300): XtreamContent[] {
    const normalizedQuery = this.normalizeSearchText(query);
    if (!normalizedQuery) return [];

    const ranked = items
      .map((item) => {
        const normalizedName = this.normalizeSearchText(item.name || '');
        let score = -1;
        if (normalizedName.startsWith(normalizedQuery)) {
          score = 3;
        } else if (normalizedName.includes(normalizedQuery)) {
          score = 2;
        } else if (normalizedName.split(' ').some((word) => word.startsWith(normalizedQuery))) {
          score = 1;
        }
        return { item, score };
      })
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score || (a.item.name || '').localeCompare(b.item.name || ''))
      .slice(0, limit)
      .map((entry) => entry.item);

    return ranked;
  }

  buildStreamUrl(
    streamId: string,
    streamType: 'live' | 'movie' | 'series' | 'vod',
    extension?: string
  ): string {
    const normalizedType = streamType === 'vod' ? 'movie' : streamType;
    const defaultExtension = normalizedType === 'live' ? 'm3u8' : 'mp4';
    const resolvedExtension = extension || defaultExtension;
    const pathType = normalizedType === 'live' ? 'live' : normalizedType;
    const encodedUsername = encodeURIComponent(this.username);
    const encodedPassword = encodeURIComponent(this.password);
    const encodedStreamId = encodeURIComponent(streamId);
    const proto = this.protocol || 'http';
    return desktopProxyUrl(`${proto}://${this.host}/${pathType}/${encodedUsername}/${encodedPassword}/${encodedStreamId}.${resolvedExtension}`);
  }

  getLiveStreamURLVariants(streamId: string, extensions: string[] = ['m3u8', 'ts', 'mp4']): string[] {
    const variants: string[] = [];
    const encodedUsername = encodeURIComponent(this.username);
    const encodedPassword = encodeURIComponent(this.password);
    const encodedStreamId = encodeURIComponent(streamId);
    extensions.forEach((extension) => {
      variants.push(this.buildStreamUrl(streamId, 'live', extension));
      variants.push(desktopProxyUrl(`${this.protocol}://${this.host}/${encodedUsername}/${encodedPassword}/${encodedStreamId}.${extension}`));
    });
    return Array.from(new Set(variants));
  }

  decodeStreamUrl(url: string): string {
    try {
      return decodeURIComponent(url);
    } catch (error) {
      return url;
    }
  }

  preparePlaybackUrl(url: string): string {
    return desktopProxyUrl(url);
  }

  async getSeriesInfo(seriesId: string): Promise<XtreamSeriesInfo> {
    const params = this.getParams('get_series_info');
    params.series_id = seriesId;
    try {
      const response = await this.api.get(this.getBaseURL(), { params });
      return response.data;
    } catch (err: any) {
      console.error('❌ Erro na requisição de série:', err.message);
      console.error('Status:', err.response?.status);
      console.error('Dados:', err.response?.data);
      throw err;
    }
  }

  getStreamURL(streamId: string, streamType: 'live' | 'movie' | 'series'): string {
    return this.buildStreamUrl(
      streamId,
      streamType,
      streamType === 'live' ? 'm3u8' : 'mp4'
    );
  }
}

const xtreamAPI = new XtreamAPI();
// eslint-disable-next-line import/no-anonymous-default-export
export default xtreamAPI;
